import * as React from "react";
import "./index.scss";
import POWER_ICON from "../../assets/power.svg";
import BANDWIDTH_ICON from "../../assets/bandwidth.svg";
import DAPPSO_RESOURCE from "../../assets/dappso-resource.png";
import INFO_ICON from "../../assets/info.svg";
import BLUE_BRICK from "../../assets/blue-brick.png";
import PINK_BRICK from "../../assets/pink-brick.png";
import ArrowRight from "../../assets/right.svg";
import Loading from "components/loading";
import { ellipsis, formatSmartBalance as fb } from "lib/format";
import {
  tronWeb,
  formatResource,
  getResourceDistributionInTrx, getV2StakedTrx,
} from "lib/tron";
import t, { getLocale } from "locales";
import ReactTooltip from "react-tooltip";
import { getAccounts } from "lib/sdk";

const RESOURCE_MANAGEMENT_URLS = {
  zh: "https://resource.dappso.com/zh",
  en: "https://resource.dappso.com/en",
};

const Progress = ({ bgImg, value, max, bgColor }) => {
  return (
    <div
      className="progress-wrap"
      style={{ backgroundColor: value > 0 ? bgColor : "#eaecf6" }}
    >
      <div
        style={{
          backgroundImage: `url(${bgImg})`,
          backgroundRepeat: "repeat-x",
          backgroundPosition: "0 0",
          backgroundSize: "4px 8px",
          width: max === 0 ? 0 : `${(value * 100) / max}%`,
          maxWidth: "calc(100% - 4px)",
        }}
        className="progress"
      ></div>
    </div>
  );
};

interface Props {
  showResourceManagement: boolean;
  stakingVersion: number;
  account: {
    address: string;
    accountInfo: any;
    resource: any;
  };
}

export class AccountInfo extends React.Component<Props, any> {
  componentWillReceiveProps() {
    ReactTooltip.rebuild();
  }

  render() {
    const { account, stakingVersion } = this.props;
    const resourceManagementUrl = RESOURCE_MANAGEMENT_URLS[getLocale()];
    const empty = {};
    const resource = account.resource || empty;
    const balance = resource.balance;
    let energy_balance = resource.totalEnergy - resource.totalEnergyUsed;

    if (energy_balance < 0) energy_balance = 0;

    const energy_max = resource.totalEnergy;
    let bandwidth_balance = resource.totalbp - resource.totalbpUsed;
    if (bandwidth_balance < 0) bandwidth_balance = 0;

    const bandwidth_max = resource.totalbp;
    const { energy, bp } = stakingVersion == 2 ? getV2StakedTrx(account.accountInfo) : getResourceDistributionInTrx(account.accountInfo);

    return (
      <div className="account-info">
        <p className="address">{ellipsis(account.address, 11, 10)}</p>
        <p className="balance">{`${balance ? fb(tronWeb.fromSun(balance)) : "~"
          } TRX`}</p>
        {resource === empty ? (
          <Loading />
        ) : (
          <div className="power">
            <div className="row">
              <div className="icon-wrap">
                <img src={POWER_ICON} alt="power" />
              </div>
              <div className="power-row-inner">
                <div className="row space-between">
                  <span className="key">{stakingVersion === 1 && `V${stakingVersion} `}{t("energy")}</span>
                  <p className="row flex-end percent-value">
                    <span>{formatResource(energy_balance, 1)}</span>
                    <span className="inactive">{`/ ${formatResource(
                      energy_max,
                      1,
                    )}`}</span>
                  </p>
                </div>
                <p className="row flex-end bottom-row">
                  <span className="value">{`${fb(energy, 2)} TRX`}</span>
                  <img
                    src={INFO_ICON}
                    data-tip
                    data-for="energyDetail"
                    alt="energyDetail"
                  />
                </p>

                <Progress
                  value={energy_balance}
                  max={energy_max}
                  bgImg={PINK_BRICK}
                  bgColor={"rgba(255, 130, 182, 0.1)"}
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: "1rem" }}>
              <div className="icon-wrap" style={{ backgroundColor: "#F2F3FF" }}>
                <img src={BANDWIDTH_ICON} alt="bandwidth" />
              </div>
              <div className="power-row-inner">
                <p className="row space-between">
                  <span className="key">{stakingVersion === 1 && `V${stakingVersion} `}{t("bandwidth")}</span>
                  <span className="row percent-value">
                    <span>{formatResource(bandwidth_balance, 0)}</span>
                    <span className="inactive">{`/ ${formatResource(
                      bandwidth_max,
                      0,
                    )}`}</span>
                  </span>
                </p>
                <p className="row flex-end  bottom-row">
                  <span className="value">{`${fb(bp, 2)} TRX`}</span>
                  <img
                    src={INFO_ICON}
                    data-tip
                    data-for="bpDetail"
                    alt="bpDetail"
                  />
                </p>

                <Progress
                  value={bandwidth_balance}
                  max={bandwidth_max}
                  bgImg={BLUE_BRICK}
                  bgColor={"rgba(130, 135, 255, 0.1)"}
                />
              </div>
            </div>
            {this.renderResourceTips()}
            {this.props.showResourceManagement && (
              <div className="link-to-stake-v2">
                <div className="icon-wrap">
                  <img src={DAPPSO_RESOURCE} alt="dappso resource" />
                </div>
                <a
                  className="link"
                  href={resourceManagementUrl}
                  onClick={async () => {
                    const wallet_address = (await getAccounts())[0]
                    
                    gtag(
                      'event',
                      'tron_resource_dappso',
                      {
                        actionType: "click",
                        caip2: "tip174:00000000000000001ebf88508a03865c",
                        wallet_address,
                        url: resourceManagementUrl,
                        title: t('link_resource_management_desc')
                      }
                    )
                  }}
                >
                  <div className="title">{t('link_resource_management_desc')}</div>
                  <div className="tail">
                    <img src={ArrowRight} alt="power" />
                  </div>
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  renderResourceTips = () => {
    const { account, stakingVersion } = this.props;
    const { self_energy, d_energy, self_bp, d_bp } = stakingVersion == 2 ? getV2StakedTrx(account.accountInfo) :
      getResourceDistributionInTrx(account.accountInfo);

    return [
      <ReactTooltip
        id="energyDetail"
        type="light"
        place="bottom"
        key="0"
        className="customeTipTheme"
      >
        <div className="tips-container">
          <span>{t("freeze_amount")}{` V${stakingVersion}`}</span>
          <div>
            <p>
              <label>{t("self")}</label>
              <span>{fb(self_energy, 2)} TRX</span>
            </p>
            <p>
              <label>{t("others")}</label>
              <span>{fb(d_energy, 2)} TRX</span>
            </p>
          </div>
        </div>
      </ReactTooltip>,

      <ReactTooltip
        id="bpDetail"
        type="light"
        place="bottom"
        key="1"
        className="customeTipTheme"
      >
        <div className="tips-container">
          <span>{t("freeze_amount")}{` V${stakingVersion}`}</span>
          <div>
            <p>
              <label>{t("self")}</label>
              <span>{fb(self_bp, 2)} TRX</span>
            </p>
            <p>
              <label>{t("others")}</label>
              <span>{fb(d_bp, 2)} TRX</span>
            </p>
          </div>
        </div>
      </ReactTooltip>,
    ];
  };
}

export default AccountInfo;
